import React from 'react';
import { motion } from 'framer-motion';

const ServiceIcon = ({ type, className = '' }) => {
  const iconVariants = {
    rest: { scale: 1, rotate: 0 },
    hover: { 
      scale: 1.1, 
      rotate: [0, -5, 5, -5, 0],
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const pathVariants = {
    rest: { pathLength: 1, opacity: 1 },
    hover: { 
      pathLength: 1, 
      opacity: 1,
      transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const pulseVariants = {
    rest: { scale: 1, opacity: 0.8 },
    hover: { 
      scale: 1.15, 
      opacity: 1,
      transition: { 
        duration: 0.4, 
        ease: [0.22, 1, 0.36, 1],
        repeat: Infinity,
        repeatType: "reverse"
      }
    }
  };

  const icons = {
    web: (
      <motion.svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`${className} web-icon-svg`}
        variants={iconVariants}
        initial="rest"
        whileHover="hover"
      >
        <defs>
          <linearGradient id="webGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="1" />
            <stop offset="50%" stopColor="#60a5fa" stopOpacity="1" />
            <stop offset="100%" stopColor="#93c5fd" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="webGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="webGradientAnimated" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="1">
              <animate attributeName="stop-opacity" values="1;0.6;1" dur="2s" repeatCount="indefinite" />
            </stop>
            <stop offset="50%" stopColor="#60a5fa" stopOpacity="1">
              <animate attributeName="stop-opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="#93c5fd" stopOpacity="1">
              <animate attributeName="stop-opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
          <filter id="webGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="webGlowStrong">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Browser Window Frame - Complete and visible */}
        <rect
          x="8"
          y="12"
          width="48"
          height="40"
          rx="3"
          stroke="url(#webGradient)"
          strokeWidth="2"
          fill="none"
          className="web-browser-frame"
        />
        
        {/* Browser Header Bar */}
        <rect
          x="8"
          y="12"
          width="48"
          height="8"
          rx="3"
          fill="url(#webGradient2)"
          fillOpacity="0.2"
          className="web-header-bar"
        />
        
        {/* Browser Control Dots - Animated */}
        <circle
          cx="14"
          cy="16"
          r="1.5"
          fill="url(#webGradientAnimated)"
          filter="url(#webGlow)"
          className="web-dot web-dot-1"
        />
        <circle
          cx="20"
          cy="16"
          r="1.5"
          fill="url(#webGradientAnimated)"
          filter="url(#webGlow)"
          className="web-dot web-dot-2"
        />
        <circle
          cx="26"
          cy="16"
          r="1.5"
          fill="url(#webGradientAnimated)"
          filter="url(#webGlow)"
          className="web-dot web-dot-3"
        />
        
        {/* Code Brackets - Complete */}
        <path
          d="M18 26 L14 30 L18 34"
          stroke="url(#webGradient)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          className="web-code-bracket web-code-bracket-left"
        />
        <path
          d="M46 26 L50 30 L46 34"
          stroke="url(#webGradient)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          className="web-code-bracket web-code-bracket-right"
        />
        
        {/* Code Lines - Complete and animated */}
        <path
          d="M24 30 L40 30"
          stroke="url(#webGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="web-code-line web-code-line-1"
        />
        <path
          d="M22 36 L38 36"
          stroke="url(#webGradient)"
          strokeWidth="1.8"
          strokeLinecap="round"
          className="web-code-line web-code-line-2"
        />
        <path
          d="M26 42 L36 42"
          stroke="url(#webGradient)"
          strokeWidth="1.8"
          strokeLinecap="round"
          className="web-code-line web-code-line-3"
        />
        
        {/* Network Nodes - Animated pulse */}
        <circle
          cx="32"
          cy="30"
          r="2.5"
          fill="url(#webGradientAnimated)"
          filter="url(#webGlowStrong)"
          className="web-node web-node-center"
        />
        <circle
          cx="28"
          cy="36"
          r="1.8"
          fill="url(#webGradient)"
          filter="url(#webGlow)"
          className="web-node web-node-left"
        />
        <circle
          cx="36"
          cy="36"
          r="1.8"
          fill="url(#webGradient)"
          filter="url(#webGlow)"
          className="web-node web-node-right"
        />
        
        {/* Connection Lines - Complete */}
        <path
          d="M32 30 L28 36 M32 30 L36 36"
          stroke="url(#webGradient)"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeOpacity="0.7"
          className="web-connection-line"
        />
        
        {/* Decorative Elements - Corner Accents */}
        <path
          d="M8 12 L12 12 L12 16"
          stroke="url(#webGradient)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M56 12 L52 12 L52 16"
          stroke="url(#webGradient)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </motion.svg>
    ),
    mobile: (
      <motion.svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`${className} mobile-icon-svg`}
        variants={iconVariants}
        initial="rest"
        whileHover="hover"
      >
        <defs>
          <linearGradient id="mobileGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="1" />
            <stop offset="50%" stopColor="#60a5fa" stopOpacity="1" />
            <stop offset="100%" stopColor="#93c5fd" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="mobileGradientAnimated" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="1">
              <animate attributeName="stop-opacity" values="1;0.7;1" dur="2s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="1">
              <animate attributeName="stop-opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
          <filter id="mobileGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="mobileGlowStrong">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        {/* Phone Frame */}
        <rect
          x="18"
          y="8"
          width="28"
          height="48"
          rx="4"
          stroke="url(#mobileGradient)"
          strokeWidth="2"
          fill="none"
          className="mobile-frame"
        />
        {/* Screen */}
        <rect
          x="22"
          y="14"
          width="20"
          height="32"
          rx="2"
          stroke="url(#mobileGradient)"
          strokeWidth="1.8"
          fill="none"
          className="mobile-screen"
        />
        {/* Home Button */}
        <circle
          cx="32"
          cy="50"
          r="2.5"
          fill="url(#mobileGradientAnimated)"
          filter="url(#mobileGlowStrong)"
          className="mobile-home-button"
        />
        {/* Notch */}
        <path
          d="M26 10 L38 10"
          stroke="url(#mobileGradient)"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="mobile-notch"
        />
        {/* Signal Indicator */}
        <circle
          cx="32"
          cy="22"
          r="2"
          fill="url(#mobileGradientAnimated)"
          filter="url(#mobileGlow)"
          className="mobile-signal mobile-signal-1"
        />
        {/* App Icons */}
        <path
          d="M28 26 L36 26 M28 30 L36 30 M28 34 L32 34"
          stroke="url(#mobileGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="mobile-app-icon mobile-app-icon-1"
        />
        <path
          d="M30 38 L34 38"
          stroke="url(#mobileGradient)"
          strokeWidth="1.5"
          strokeLinecap="round"
          className="mobile-app-icon mobile-app-icon-2"
        />
      </motion.svg>
    ),
    backend: (
      <motion.svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`${className} backend-icon-svg`}
        variants={iconVariants}
        initial="rest"
        whileHover="hover"
      >
        <defs>
          <linearGradient id="backendGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="1" />
            <stop offset="50%" stopColor="#60a5fa" stopOpacity="1" />
            <stop offset="100%" stopColor="#93c5fd" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="backendGradientAnimated" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="1">
              <animate attributeName="stop-opacity" values="1;0.7;1" dur="2s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="1">
              <animate attributeName="stop-opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
          <filter id="backendGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="backendGlowStrong">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        {/* Server Frame */}
        <rect
          x="12"
          y="16"
          width="40"
          height="32"
          rx="2"
          stroke="url(#backendGradient)"
          strokeWidth="2"
          fill="none"
          className="backend-frame"
        />
        {/* Code Lines */}
        <path
          d="M20 24 L44 24"
          stroke="url(#backendGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="backend-code-line backend-code-line-1"
        />
        <path
          d="M20 28 L44 28"
          stroke="url(#backendGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="backend-code-line backend-code-line-2"
        />
        <path
          d="M20 32 L36 32"
          stroke="url(#backendGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="backend-code-line backend-code-line-3"
        />
        <path
          d="M20 36 L40 36"
          stroke="url(#backendGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="backend-code-line backend-code-line-4"
        />
        {/* Status Indicators */}
        <circle
          cx="18"
          cy="24"
          r="2.5"
          fill="url(#backendGradientAnimated)"
          filter="url(#backendGlowStrong)"
          className="backend-status backend-status-1"
        />
        <circle
          cx="18"
          cy="28"
          r="2.5"
          fill="url(#backendGradientAnimated)"
          filter="url(#backendGlowStrong)"
          className="backend-status backend-status-2"
        />
        {/* Antennas */}
        <path
          d="M16 12 L20 8 L24 12"
          stroke="url(#backendGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          className="backend-antenna backend-antenna-left"
        />
        <path
          d="M48 12 L44 8 L40 12"
          stroke="url(#backendGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          className="backend-antenna backend-antenna-right"
        />
        <path
          d="M20 8 L20 16"
          stroke="url(#backendGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="backend-antenna-line backend-antenna-line-left"
        />
        <path
          d="M44 8 L44 16"
          stroke="url(#backendGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="backend-antenna-line backend-antenna-line-right"
        />
      </motion.svg>
    ),
    cloud: (
      <motion.svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`${className} cloud-icon-svg`}
        variants={iconVariants}
        initial="rest"
        whileHover="hover"
      >
        <defs>
          <linearGradient id="cloudGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="1" />
            <stop offset="50%" stopColor="#60a5fa" stopOpacity="1" />
            <stop offset="100%" stopColor="#93c5fd" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="cloudGradientAnimated" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="1">
              <animate attributeName="stop-opacity" values="1;0.7;1" dur="2.5s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="1">
              <animate attributeName="stop-opacity" values="0.8;1;0.8" dur="2.5s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
          <filter id="cloudGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="cloudGlowStrong">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        {/* Cloud Shape */}
        <path
          d="M20 40 C16 40, 12 36, 12 32 C12 28, 16 24, 20 24 C22 20, 26 18, 30 20 C32 16, 36 14, 40 16 C44 14, 48 18, 48 22 C52 22, 54 26, 52 30 C54 30, 56 32, 56 34 C56 38, 52 40, 48 40 Z"
          stroke="url(#cloudGradient)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          className="cloud-shape"
        />
        {/* Cloud Nodes */}
        <circle
          cx="28"
          cy="32"
          r="2.5"
          fill="url(#cloudGradientAnimated)"
          filter="url(#cloudGlowStrong)"
          className="cloud-node cloud-node-1"
        />
        <circle
          cx="36"
          cy="32"
          r="2.5"
          fill="url(#cloudGradientAnimated)"
          filter="url(#cloudGlowStrong)"
          className="cloud-node cloud-node-2"
        />
        {/* Data Flow Arrows */}
        <path
          d="M24 28 L20 24"
          stroke="url(#cloudGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="cloud-arrow cloud-arrow-left"
        />
        <path
          d="M44 24 L40 28"
          stroke="url(#cloudGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="cloud-arrow cloud-arrow-right"
        />
        {/* Connection Lines */}
        <path
          d="M32 20 L32 16"
          stroke="url(#cloudGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="cloud-connection cloud-connection-top"
        />
        <path
          d="M32 44 L32 48"
          stroke="url(#cloudGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="cloud-connection cloud-connection-bottom"
        />
        {/* Additional Cloud Elements */}
        <circle
          cx="32"
          cy="32"
          r="1.5"
          fill="url(#cloudGradient)"
          filter="url(#cloudGlow)"
          className="cloud-center"
        />
      </motion.svg>
    ),
    uiux: (
      <motion.svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`${className} uiux-icon-svg`}
        variants={iconVariants}
        initial="rest"
        whileHover="hover"
      >
        <defs>
          <linearGradient id="uiuxGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="1" />
            <stop offset="50%" stopColor="#60a5fa" stopOpacity="1" />
            <stop offset="100%" stopColor="#93c5fd" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="uiuxGradientAnimated" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="1">
              <animate attributeName="stop-opacity" values="1;0.7;1" dur="2s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="1">
              <animate attributeName="stop-opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
          <filter id="uiuxGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="uiuxGlowStrong">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        {/* Device Frame */}
        <path
          d="M16 20 L32 12 L48 20 L48 44 L32 52 L16 44 Z"
          stroke="url(#uiuxGradient)"
          strokeWidth="2.5"
          fill="none"
          className="uiux-frame"
        />
        {/* Perspective Lines */}
        <path
          d="M16 20 L32 28 L48 20 M32 28 L32 52 M16 44 L48 44"
          stroke="url(#uiuxGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="uiux-perspective"
        />
        {/* UI Elements */}
        <circle
          cx="24"
          cy="28"
          r="2.5"
          fill="url(#uiuxGradientAnimated)"
          filter="url(#uiuxGlowStrong)"
          className="uiux-element uiux-element-1"
        />
        <circle
          cx="40"
          cy="28"
          r="2.5"
          fill="url(#uiuxGradientAnimated)"
          filter="url(#uiuxGlowStrong)"
          className="uiux-element uiux-element-2"
        />
        {/* Content Lines */}
        <path
          d="M24 36 L40 36"
          stroke="url(#uiuxGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="uiux-content uiux-content-1"
        />
        <path
          d="M24 40 L32 40"
          stroke="url(#uiuxGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          className="uiux-content uiux-content-2"
        />
        {/* Top Bar */}
        <path
          d="M28 16 L36 16"
          stroke="url(#uiuxGradient)"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="uiux-topbar"
        />
        {/* Additional UI Elements */}
        <rect
          x="26"
          y="30"
          width="4"
          height="3"
          rx="0.5"
          fill="url(#uiuxGradient)"
          fillOpacity="0.6"
          className="uiux-box uiux-box-1"
        />
        <rect
          x="34"
          y="30"
          width="4"
          height="3"
          rx="0.5"
          fill="url(#uiuxGradient)"
          fillOpacity="0.6"
          className="uiux-box uiux-box-2"
        />
      </motion.svg>
    ),
  };

  return icons[type] || icons.web;
};

export default ServiceIcon;

